import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowRight, ArrowLeft, Loader2, ShieldAlert, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "basics", label: "Basics" },
  { key: "statement", label: "Personal Statement" },
  { key: "reentry", label: "Reentry Plan" },
  { key: "rehab", label: "Rehabilitation" },
  { key: "exhibits", label: "Exhibits" },
];

const FIELDS_BY_STEP = {
  basics: [
    { k: "full_name", label: "Full Name", required: true },
    { k: "din", label: "DIN", required: true },
    { k: "facility", label: "Facility", required: true },
    { k: "email", label: "Email (for delivery)", type: "email", required: true },
    { k: "offense_summary", label: "Offense Summary", textarea: true, rows: 4 },
  ],
  statement: [
    { k: "accountability", label: "Accountability", textarea: true, rows: 4 },
    { k: "reflection", label: "Reflection", textarea: true, rows: 4 },
    { k: "rehab_efforts", label: "Rehabilitation Efforts", textarea: true, rows: 4 },
    { k: "personal_growth", label: "Personal Growth", textarea: true, rows: 4 },
    { k: "future_intentions", label: "Future Intentions", textarea: true, rows: 4 },
  ],
  reentry: [
    { k: "housing", label: "Housing Stability", textarea: true, rows: 3 },
    { k: "employment", label: "Employment Plan", textarea: true, rows: 3 },
    { k: "financial", label: "Financial Stability", textarea: true, rows: 3 },
    { k: "community_support", label: "Community Support", textarea: true, rows: 3 },
    { k: "risk_reduction", label: "Risk Reduction Strategy", textarea: true, rows: 3 },
  ],
  rehab: [
    { k: "programs_completed", label: "Programs Completed", textarea: true, rows: 4 },
    { k: "behavioral_record", label: "Behavioral Record", textarea: true, rows: 3 },
    { k: "institutional_conduct", label: "Institutional Conduct", textarea: true, rows: 3 },
  ],
  exhibits: [
    { k: "exhibits_text", label: "Supporting Document Index (one per line)", textarea: true, rows: 6,
      placeholder: "Exhibit A — Program completion certificates\nExhibit B — Letters of support\nExhibit C — Educational records" },
  ],
};

const INITIAL = {
  full_name: "", din: "", facility: "", email: "", offense_summary: "",
  accountability: "", reflection: "", rehab_efforts: "", personal_growth: "", future_intentions: "",
  housing: "", employment: "", financial: "", community_support: "", risk_reduction: "",
  programs_completed: "", behavioral_record: "", institutional_conduct: "",
  exhibits_text: "",
};

const STEP_CIRCLE = {
  done: "bg-blue-700 border-amber-400 text-white",
  active: "border-amber-400 text-amber-400",
  todo: "border-zinc-700 text-zinc-500",
};

function getStepStateClass(done, active) {
  if (done) return STEP_CIRCLE.done;
  if (active) return STEP_CIRCLE.active;
  return STEP_CIRCLE.todo;
}

function renderSubmitLabel(submitting, isLast) {
  if (submitting) return <Loader2 className="h-4 w-4 animate-spin" />;
  const text = isLast ? "Continue to Payment" : "Next";
  return <>{text} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></>;
}

export default function IntakeParole() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepKey = STEPS[step].key;
  const fields = FIELDS_BY_STEP[stepKey];
  const isLast = step === STEPS.length - 1;

  const canAdvance = () => {
    if (stepKey !== "basics") return true;
    return form.full_name && form.din && form.facility && form.email;
  };

  const next = () => { if (canAdvance()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!isLast) { next(); return; }
    setSubmitting(true);
    try {
      const { data: intake } = await api.post("/intake/parole", form);
      const { data: checkout } = await api.post("/checkout/session", {
        product_id: "parole_packet",
        intake_id: intake.id,
        email: form.email,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-zinc-100 min-h-screen pt-12 pb-24">
      <div className="container-pl max-w-2xl">
        <p className="overline mb-4">Parole Packet · $99.99</p>
        <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50 leading-tight mb-3 accent-rule">
          Parole Packet Intake
        </h1>
        <p className="text-base text-zinc-400 mb-8 max-w-xl leading-relaxed">
          Five short sections. Your answers are formatted into a court-ready packet for the NYS Board of Parole.
        </p>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8" data-testid="parole-step-indicator">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className={`h-7 w-7 rounded-full border flex items-center justify-center text-xs font-mono ${getStepStateClass(done, active)}`}>
                  {done ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${done ? "bg-blue-700" : "bg-zinc-800"}`}/>}
              </div>
            );
          })}
        </div>
        <p className="text-xs uppercase tracking-[0.18em] text-amber-400 mb-6">{STEPS[step].label}</p>

        <div className="border border-amber-400/30 bg-blue-950/20 rounded-sm p-4 mb-6 flex gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-zinc-300 leading-relaxed">
            We are not attorneys and do not provide legal advice. This packet is administrative document preparation only.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="parole-intake-form">
          {fields.map((f) => (
            <div key={f.k}>
              <Label className="text-zinc-300">{f.label}{f.required && <span className="text-amber-400 ml-1">*</span>}</Label>
              {f.textarea ? (
                <Textarea rows={f.rows || 4} required={f.required} value={form[f.k]} onChange={(e) => update(f.k, e.target.value)} placeholder={f.placeholder} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid={`p-${f.k}`}/>
              ) : (
                <Input type={f.type || "text"} required={f.required} value={form[f.k]} onChange={(e) => update(f.k, e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid={`p-${f.k}`}/>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between pt-6 border-t border-zinc-800">
            {step > 0 ? (
              <Button type="button" variant="outline" onClick={back} className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-900" data-testid="p-back">
                <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} /> Back
              </Button>
            ) : <Link to="/services" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors">← Back to services</Link>}

            <Button type="submit" size="lg" disabled={submitting} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="p-next">
              {renderSubmitLabel(submitting, isLast)}
            </Button>
          </div>
          {isLast && (
            <div className="pt-2">
              <p className="display-serif text-2xl text-amber-400">$99.99 <span className="text-xs text-zinc-500 font-mono">USD · One-time</span></p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
